import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Calendar, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  RefreshCw,
  Settings,
  Trash2,
  Save,
  BookOpen
} from 'lucide-react';

const TermManagement = () => {
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [savingMetadata, setSavingMetadata] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [termToDelete, setTermToDelete] = useState(null);
  
  // Metadata edit state for each term
  const [editingMetadata, setEditingMetadata] = useState({});

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchTerms();
  }, []);

  const fetchTerms = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/api/admin/terms`, {
        withCredentials: true
      });
      setTerms(response.data);
      
      // Initialize editing metadata state for each term
      const metadataState = {};
      response.data.forEach(term => {
        metadataState[term.id] = {
          academic_year: term.academic_year || '',
          current_week: term.current_week || '',
          week_ending_date: term.week_ending_date || ''
        };
      });
      setEditingMetadata(metadataState);
    } catch (error) {
      console.error('Error fetching terms:', error);
      toast.error("Failed to fetch terms. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleMetadataChange = (termId, field, value) => {
    setEditingMetadata(prev => ({
      ...prev,
      [termId]: {
        ...prev[termId],
        [field]: value
      }
    }));
  };

  const saveTermMetadata = async (termId) => {
    const metadata = editingMetadata[termId];
    if (!metadata) return;

    // Validate academic year format
    if (metadata.academic_year && !/^\d{4}\/\d{4}$/.test(metadata.academic_year)) {
      toast.error("Academic year must be in format yyyy/yyyy (e.g., 2024/2025)");
      return;
    }

    // Validate week number
    const weekNum = parseInt(metadata.current_week);
    if (metadata.current_week && (isNaN(weekNum) || weekNum < 1 || weekNum > 18)) {
      toast.error("Week number must be between 1 and 18");
      return;
    }

    try {
      setSavingMetadata(termId);
      
      const updateData = {};
      if (metadata.academic_year) updateData.academic_year = metadata.academic_year;
      if (metadata.current_week) updateData.current_week = parseInt(metadata.current_week);
      if (metadata.week_ending_date) updateData.week_ending_date = metadata.week_ending_date;

      const response = await axios.put(
        `${API_BASE}/api/admin/terms/${termId}/metadata`,
        updateData,
        { withCredentials: true }
      );

      if (response.status === 200) {
        // Update local state with the returned term data
        setTerms(prevTerms =>
          prevTerms.map(term =>
            term.id === termId ? { ...term, ...response.data.term } : term
          )
        );
        toast.success("Term metadata saved successfully!");
      }
    } catch (error) {
      console.error('Error saving term metadata:', error);
      toast.error(error.response?.data?.detail || "Failed to save term metadata.");
    } finally {
      setSavingMetadata(null);
    }
  };

  const toggleTermStatus = async (termId, currentStatus) => {
    try {
      setUpdating(termId);
      
      const response = await axios.put(
        `${API_BASE}/api/admin/terms/${termId}/toggle`,
        { is_active: !currentStatus },
        { withCredentials: true }
      );

      if (response.status === 200) {
        // Update local state
        setTerms(prevTerms => 
          prevTerms.map(term => 
            term.id === termId 
              ? { ...term, is_active: !currentStatus }
              : { ...term, is_active: false } // Deactivate other terms
          )
        );

        toast.success(`Term ${!currentStatus ? 'activated' : 'deactivated'} successfully.`);
      }
    } catch (error) {
      console.error('Error toggling term status:', error);
      toast.error(error.response?.data?.detail || "Failed to update term status.");
    } finally {
      setUpdating(null);
    }
  };

  const handleDeleteClick = (term) => {
    if (term.is_active) {
      toast.error("Cannot delete an active term. Please deactivate it first.");
      return;
    }
    setTermToDelete(term);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!termToDelete) return;

    try {
      setDeleting(termToDelete.id);
      
      const response = await axios.delete(
        `${API_BASE}/api/admin/terms/${termToDelete.id}`,
        { withCredentials: true }
      );

      if (response.status === 200) {
        // Remove term from local state
        setTerms(prevTerms => prevTerms.filter(term => term.id !== termToDelete.id));
        toast.success("Term deleted successfully.");
      }
    } catch (error) {
      console.error('Error deleting term:', error);
      toast.error(error.response?.data?.detail || "Failed to delete term.");
    } finally {
      setDeleting(null);
      setDeleteDialogOpen(false);
      setTermToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setTermToDelete(null);
  };

  const getTermStatusColor = (isActive) => {
    return isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600';
  };

  const getTermIcon = (isActive) => {
    return isActive ? (
      <CheckCircle className="h-4 w-4 text-green-600" />
    ) : (
      <XCircle className="h-4 w-4 text-gray-400" />
    );
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Term Management
          </CardTitle>
          <CardDescription>
            Manage active academic terms and toggle term status
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
            <span className="ml-3 text-gray-600">Loading terms...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const activeTerm = terms.find(term => term.is_active);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Term Management
          </CardTitle>
          <CardDescription>
            Manage active academic terms and toggle term status. Only one term can be active at a time.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Active Term Alert */}
          {activeTerm && (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                <strong>{activeTerm.name}</strong> is currently active. 
                {activeTerm.academic_year && ` Academic Year: ${activeTerm.academic_year}.`}
                {activeTerm.current_week && ` Week ${activeTerm.current_week}.`}
              </AlertDescription>
            </Alert>
          )}

          {!activeTerm && (
            <Alert className="border-orange-200 bg-orange-50">
              <AlertCircle className="h-4 w-4 text-orange-600" />
              <AlertDescription className="text-orange-800">
                No term is currently active. Please activate a term to enable teacher subscriptions.
              </AlertDescription>
            </Alert>
          )}

          {/* Terms List */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {terms.map((term) => (
              <Card 
                key={term.id}
                className={`transition-all duration-200 ${
                  term.is_active 
                    ? 'border-green-200 bg-green-50/50 shadow-md' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      {getTermIcon(term.is_active)}
                      {term.name}
                    </CardTitle>
                    <Badge className={getTermStatusColor(term.is_active)}>
                      {term.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-sm text-gray-600">
                    Created: {new Date(term.created_at).toLocaleDateString()}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">
                      {term.is_active ? 'Deactivate Term' : 'Activate Term'}
                    </span>
                    <Switch
                      checked={term.is_active}
                      onCheckedChange={() => toggleTermStatus(term.id, term.is_active)}
                      disabled={updating === term.id || deleting === term.id}
                      className="data-[state=checked]:bg-green-600"
                    />
                  </div>
                  
                  {/* Metadata Fields - Only shown for active term */}
                  {term.is_active && (
                    <div className="pt-3 border-t border-green-200 space-y-3">
                      <div className="flex items-center gap-2 text-green-700 font-medium text-sm">
                        <BookOpen className="h-4 w-4" />
                        Lesson Notes Settings
                      </div>
                      
                      {/* Academic Year - Set once per term */}
                      <div className="space-y-1">
                        <Label htmlFor={`academic_year_${term.id}`} className="text-xs text-gray-600">
                          Academic Year (e.g., 2024/2025)
                        </Label>
                        <Input
                          id={`academic_year_${term.id}`}
                          placeholder="2024/2025"
                          value={editingMetadata[term.id]?.academic_year || ''}
                          onChange={(e) => handleMetadataChange(term.id, 'academic_year', e.target.value)}
                          className="h-8 text-sm"
                        />
                      </div>
                      
                      {/* Current Week - Updated weekly */}
                      <div className="space-y-1">
                        <Label htmlFor={`current_week_${term.id}`} className="text-xs text-gray-600">
                          Current Week (1-18)
                        </Label>
                        <Input
                          id={`current_week_${term.id}`}
                          type="number"
                          min="1"
                          max="18"
                          placeholder="1"
                          value={editingMetadata[term.id]?.current_week || ''}
                          onChange={(e) => handleMetadataChange(term.id, 'current_week', e.target.value)}
                          className="h-8 text-sm"
                        />
                      </div>
                      
                      {/* Week Ending Date - Updated weekly */}
                      <div className="space-y-1">
                        <Label htmlFor={`week_ending_${term.id}`} className="text-xs text-gray-600">
                          Week Ending Date
                        </Label>
                        <Input
                          id={`week_ending_${term.id}`}
                          type="date"
                          value={editingMetadata[term.id]?.week_ending_date || ''}
                          onChange={(e) => handleMetadataChange(term.id, 'week_ending_date', e.target.value)}
                          className="h-8 text-sm"
                        />
                      </div>
                      
                      {/* Save Metadata Button */}
                      <Button
                        size="sm"
                        onClick={() => saveTermMetadata(term.id)}
                        disabled={savingMetadata === term.id}
                        className="w-full bg-green-600 hover:bg-green-700"
                      >
                        {savingMetadata === term.id ? (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            Save Settings
                          </>
                        )}
                      </Button>
                      
                      <p className="text-xs text-gray-500 italic">
                        These settings auto-populate lesson notes forms
                      </p>
                    </div>
                  )}
                  
                  {/* Delete Button */}
                  <div className="pt-2 border-t border-gray-200">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteClick(term)}
                      disabled={term.is_active || deleting === term.id || updating === term.id}
                      className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {deleting === term.id ? 'Deleting...' : 'Delete Term'}
                    </Button>
                    {term.is_active && (
                      <p className="text-xs text-gray-500 mt-1 text-center">
                        Deactivate to delete
                      </p>
                    )}
                  </div>
                  
                  {updating === term.id && (
                    <div className="flex items-center justify-center py-2">
                      <RefreshCw className="h-4 w-4 animate-spin text-blue-600" />
                      <span className="ml-2 text-sm text-gray-600">Updating...</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Refresh Button */}
          <div className="flex justify-end">
            <Button 
              onClick={fetchTerms} 
              variant="outline"
              disabled={loading}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh Terms
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Term Management Guidelines */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Term Management Guidelines
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900">Single Active Term</p>
                <p className="text-sm text-gray-600">Only one academic term can be active at any time. Activating a new term automatically deactivates others.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900">Automatic Subscription Linking</p>
                <p className="text-sm text-gray-600">All new teacher subscriptions are automatically linked to the currently active term.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900">Subscription Validity</p>
                <p className="text-sm text-gray-600">Teacher subscriptions remain valid for the entire duration of the active term until it's deactivated.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900">Payment Integration</p>
                <p className="text-sm text-gray-600">All subscription payments are processed through Paystack gateway for secure transactions.</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Term</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{termToDelete?.name}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDeleteCancel}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TermManagement;